// Özel alan adı (custom domain) yardımcıları. Saf fonksiyonlar; `node --test` ile doğrudan test edilir
// (tests/customDomainHost.spec.mjs). Müşteri kendi alan adını CNAME ile yönlendirdiğinde host nginx
// web'i `/`, EPayment API'yi aynı origin `/api/` altında sunar; bu modül o modu tanır ve
// uygulama monte edilmeden önce firmanın çözülüp çözülmediğini denetler.

import { pick } from "./customDomainLogin.mjs";

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";
const TENANT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const IPV4_PATTERN = /^\d{1,3}(?:\.\d{1,3}){3}$/;
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;
const DEFAULT_BOOTSTRAP_TIMEOUT_MS = 15000;

export const CUSTOM_DOMAIN_UNAVAILABLE_TITLE =
  "Bu alan adı Finmony e-Kolay Tahsilat için tanımlı değil veya şu anda etkin değil.";
export const CUSTOM_DOMAIN_UNAVAILABLE_DESCRIPTION =
  "Adresi doğru yazdığınızdan emin olun. Sorun devam ederse ödeme bağlantısını size gönderen firmayla iletişime geçin.";

/**
 * @param {unknown} value
 * @returns {string}
 */
const normalizeHostname = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\.+$/, "");

/**
 * @param {string} host
 * @returns {boolean}
 */
const isIpAddress = (host) => {
  const unbracketed = host.replace(/^\[/, "").replace(/\]$/, "");
  return IPV4_PATTERN.test(unbracketed) || unbracketed.includes(":");
};

/**
 * Host, kiracı kök alan adının (örn. finmony.com) dışında, müşteriye ait bir alan adı mı?
 * localhost / *.localhost / IP adresleri / tek etiketli host'lar ve kök alan adı bilinmeyen durumlar
 * kapalı (false) kalır.
 * @param {string | null | undefined} hostname
 * @param {string | null | undefined} rootDomain
 * @returns {boolean}
 */
export const isCustomDomainHost = (hostname, rootDomain) => {
  const host = normalizeHostname(hostname);
  const root = normalizeHostname(rootDomain);
  if (!host || !root) return false;
  if (isIpAddress(host)) return false;
  if (host === "localhost" || host.endsWith(".localhost")) return false;
  if (!host.includes(".")) return false;
  if (host === root || host.endsWith(`.${root}`)) return false;
  return true;
};

/**
 * @param {string} origin
 * @returns {string}
 */
export const buildSameOriginApiBase = (origin) => `${String(origin ?? "").trim().replace(/\/+$/, "")}/api`;

/**
 * Kiracı kısa adını doğrular (yalnız küçük harf, rakam ve tire). Geçersizse null.
 * @param {unknown} value
 * @returns {string | null}
 */
export const sanitizeTenantSlug = (value) => {
  if (typeof value !== "string") return null;
  const slug = value.trim().toLowerCase();
  return TENANT_SLUG_PATTERN.test(slug) ? slug : null;
};

/**
 * Logo adresini yalnız güvenli biçimlerde kabul eder (https veya data:image); aksi halde null.
 * @param {unknown} value
 * @returns {string | null}
 */
export const sanitizeLogoUrl = (value) => {
  if (typeof value !== "string") return null;
  const url = value.trim();
  if (!url) return null;
  if (/^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,/i.test(url)) return url;
  return /^https:\/\/[^\s"'<>]+$/i.test(url) ? url : null;
};

/**
 * Yerelleştirilmiş metnin Türkçesini, yoksa İngilizcesini döndürür (portalın Türkçe çözümüyle aynı); boşsa null.
 * @param {any} text
 * @returns {string | null}
 */
const pickLocalizedText = (text) => {
  for (const value of [pick(text, "tr"), pick(text, "en")]) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
};

/**
 * Giriş ekranının portalla aynı görünmesi için marka ayarları: karşılama metni, sağ panel rengi ve
 * giriş ekranında gösterilecek alt bağlantılar. Eksik alanlar boş/null kalır; ekran portalın varsayılanlarını kullanır.
 * @param {any} branding EPayment yanıtındaki `Branding` nesnesi
 * @returns {{ welcomeTitle: string | null, welcomeSubtitle: string | null, primaryColor: string | null,
 *   footerLinks: Array<{ id: string, title: string, contentHtml: string }>,
 *   hideDefaultLinks: { help: boolean, privacy: boolean, terms: boolean } }}
 */
export const parseLoginBranding = (branding) => {
  const login = pick(branding, "login");
  const rawColor = pick(pick(branding, "brand"), "primaryColor");
  const footer = pick(branding, "footerLinks");
  const hidden = pick(footer, "hideDefaultLinks");
  const items = pick(footer, "items");

  const footerLinks = (Array.isArray(items) ? items : [])
    .map((item) => ({
      id: String(pick(item, "id") ?? "").trim(),
      title: typeof pick(item, "title") === "string" ? pick(item, "title").trim() : "",
      contentHtml: typeof pick(item, "contentHtml") === "string" ? pick(item, "contentHtml") : "",
      order: Number(pick(item, "order")) || 0,
      visible: pick(item, "isActive") === true && pick(item, "showOnLogin") === true,
    }))
    .filter((item) => item.visible && item.id && item.title)
    .sort((a, b) => a.order - b.order)
    .map(({ id, title, contentHtml }) => ({ id, title, contentHtml }));

  return {
    welcomeTitle: pickLocalizedText(pick(login, "welcomeTitle")),
    welcomeSubtitle: pickLocalizedText(pick(login, "welcomeSubtitle")),
    primaryColor: typeof rawColor === "string" && HEX_COLOR_PATTERN.test(rawColor.trim()) ? rawColor.trim() : null,
    footerLinks,
    hideDefaultLinks: {
      help: pick(hidden, "help") === true,
      privacy: pick(hidden, "privacy") === true,
      terms: pick(hidden, "terms") === true,
    },
  };
};

/**
 * EPayment `ServiceResponse<PlatformTenantBrandingDto>` yanıtından firma, kiracı ve giriş ekranının
 * ihtiyaç duyduğu marka bilgisini okur. Firma çözülemediyse (boş/null/boş GUID ya da isSuccess=false) null döner.
 * @param {any} body
 * @returns {{ companyId: string, tenantSlug: string | null, companyName: string | null,
 *   logoUrl: string | null, isGuestPaymentAllowed: boolean,
 *   loginBranding: ReturnType<typeof parseLoginBranding> } | null}
 */
export const parseTenantBrandingResponse = (body) => {
  if (!body || typeof body !== "object") return null;
  const isSuccess = body.isSuccess ?? body.IsSuccess;
  if (isSuccess === false) return null;

  const value = body.value ?? body.Value;
  if (!value || typeof value !== "object") return null;

  const rawCompanyId = value.companyId ?? value.CompanyId;
  const companyId = rawCompanyId === null || rawCompanyId === undefined ? "" : String(rawCompanyId).trim();
  if (!companyId || companyId.toLowerCase() === EMPTY_GUID) return null;

  const branding = value.branding ?? value.Branding;
  const brand = branding?.brand ?? branding?.Brand;
  const rawName = value.companyName ?? value.CompanyName;
  const guestAllowed = value.isGuestPaymentAllowed ?? value.IsGuestPaymentAllowed;

  return {
    companyId,
    tenantSlug: sanitizeTenantSlug(value.tenantSlug ?? value.TenantSlug),
    companyName: typeof rawName === "string" && rawName.trim() ? rawName.trim() : null,
    logoUrl: sanitizeLogoUrl(brand?.logoUrl ?? brand?.LogoUrl) ?? sanitizeLogoUrl(value.logoUrl ?? value.LogoUrl),
    // EPayment ucu firma ayarı okunamazsa açık (true) döner; yalnız açıkça false ise buton gizlenir.
    isGuestPaymentAllowed: guestAllowed !== false,
    loginBranding: parseLoginBranding(branding),
  };
};

/**
 * Özel alan adı modunda uygulama monte edilmeden önce firmanın bu host için çözülüp çözülmediğini denetler.
 * finmony.com / localhost host'larında hiçbir istek atmaz.
 * @param {{ hostname: string, origin: string, rootDomain: string | null | undefined,
 *   fetchImpl?: (input: string, init?: any) => Promise<any>, timeoutMs?: number }} options
 * @returns {Promise<{ status: "not-custom" } | ({ status: "ready" } & NonNullable<ReturnType<typeof parseTenantBrandingResponse>>)
 *   | { status: "unavailable" }>}
 */
export const bootstrapCustomDomain = async ({ hostname, origin, rootDomain, fetchImpl, timeoutMs }) => {
  if (!isCustomDomainHost(hostname, rootDomain)) return { status: "not-custom" };

  const doFetch = fetchImpl ?? (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return { status: "unavailable" };

  const url = `${buildSameOriginApiBase(origin)}/Payment/tenant-branding?isGuestPayment=true`;
  const controller = typeof AbortController === "function" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs ?? DEFAULT_BOOTSTRAP_TIMEOUT_MS)
    : null;

  try {
    const response = await doFetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal: controller?.signal,
    });
    if (!response || !response.ok) return { status: "unavailable" };

    const parsed = parseTenantBrandingResponse(await response.json());
    if (!parsed) return { status: "unavailable" };
    return { status: "ready", ...parsed };
  } catch {
    return { status: "unavailable" };
  } finally {
    if (timer) clearTimeout(timer);
  }
};

/**
 * Uygulama yerine kontrollü, sade Türkçe hata ekranını yazar. Yalnız textContent kullanılır.
 * @param {any} container
 * @param {any} [doc]
 */
export const renderCustomDomainUnavailable = (container, doc) => {
  if (!container) return;
  const documentRef = doc ?? globalThis.document;
  if (!documentRef) return;

  const wrapper = documentRef.createElement("main");
  wrapper.setAttribute("role", "alert");
  Object.assign(wrapper.style, {
    maxWidth: "560px",
    margin: "15vh auto 0",
    padding: "32px 24px",
    fontFamily: "Roboto, Arial, sans-serif",
    color: "#1f2937",
    textAlign: "center",
  });

  const title = documentRef.createElement("h1");
  title.textContent = CUSTOM_DOMAIN_UNAVAILABLE_TITLE;
  Object.assign(title.style, { fontSize: "20px", fontWeight: "600", lineHeight: "1.4", margin: "0 0 12px" });

  const description = documentRef.createElement("p");
  description.textContent = CUSTOM_DOMAIN_UNAVAILABLE_DESCRIPTION;
  Object.assign(description.style, { fontSize: "15px", lineHeight: "1.6", margin: "0", color: "#4b5563" });

  wrapper.appendChild(title);
  wrapper.appendChild(description);

  if (typeof container.replaceChildren === "function") {
    container.replaceChildren(wrapper);
  } else {
    container.textContent = "";
    container.appendChild(wrapper);
  }
};
